import { useEffect, useRef } from 'react';
import { useRouter } from '@funtech-int/rival-react';
import { useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';

import { clientOnly } from '@/shared/lib/clientOnly';
import { QueryParameter } from '@/shared/constants/queryParams';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { removeQueryParamsFromUrl } from '@/shared/lib/navigation';

import { useChannel } from '../../hooks/useChannelBaseInfo';
import { resolveChannelPlayback } from '../../lib/resolveChannelPlayback';
import { useChannelCardModal } from '../../hooks/useChannelCardModal';

interface ChannelUrlParamHandlerProps {
    openedChannelId: string;
}

const ChannelUrlParamHandler = ({ openedChannelId }: ChannelUrlParamHandlerProps) => {
    const { channel } = useChannel({ contentId: openedChannelId ?? '' });
    const channelCardModal = useChannelCardModal();
    const { isAuthorized } = useAuthManager();
    const router = useRouter();
    const { flags } = useFeatureFlags();
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled', 'boolean', false);
    const hasHandled = useRef(false);

    useEffect(() => {
        if (hasHandled.current) {
            return;
        }

        hasHandled.current = true;

        if (!channel?.contentId) {
            removeQueryParamsFromUrl({ router, queryParams: [QueryParameter.OPENED_CHANNEL_ID] });
            return;
        }

        const removeQueryParams = () =>
            removeQueryParamsFromUrl({ router, queryParams: [QueryParameter.OPENED_CHANNEL_ID] });

        resolveChannelPlayback(channel.viewOption, isAuthorized, {
            onNotAvailable: removeQueryParams,
            onNeedAuth: removeQueryParams,
            onShowModal: () => channelCardModal.open(channel),
            onPlay: () =>
                startPlaying({
                    router,
                    contentId: channel.contentId,
                    contentType: PlayerContentType.CHANNEL,
                    isNewPlayerEnabled,
                }),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel?.contentId]);

    return null;
};

export const ChannelUrlParamHandlerClient = clientOnly(ChannelUrlParamHandler);
